import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Editor } from "@tinymce/tinymce-react";
import { toast } from "react-toastify";
import authStorage from "../utils/authStorage";
import { BASE_URL } from "../config";

const SLUG = "medicalConsent";

// Admin page to edit the medical-condition consent disclaimer shown on the
// public signup page. Content is stored in the DB (SiteContent collection).
const MedicalConsentManage = () => {
  const editorRef = useRef(null);
  const [title, setTitle] = useState("");
  const [initialContent, setInitialContent] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Restrict to admins; mirror the guard used by other admin pages.
    const isAdmin = ["admin"].includes(authStorage.getItem("roles"));
    if (!isAdmin) {
      window.location = window.location.origin + "/dashboard";
      return;
    }
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/content/${SLUG}`);
      if (res?.data?.data) {
        setTitle(res.data.data.title || "");
        setInitialContent(res.data.data.content || "");
      }
    } catch (err) {
      console.log("Failed to load content", err);
      toast.error("Failed to load content");
    } finally {
      setLoaded(true);
    }
  };

  const handleSave = async () => {
    const content = editorRef.current ? editorRef.current.getContent() : initialContent;
    if (!content || !content.trim()) {
      toast.error("Content cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await axios({
        method: "PUT",
        url: `${BASE_URL}/content/${SLUG}`,
        headers: { "x-access-token": authStorage.getItem("auth-token") },
        data: { title, content },
      });
      toast.success("Consent content updated");
    } catch (err) {
      console.log("Failed to save content", err);
      toast.error(err?.response?.data?.message || "Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto text-left">
      <h1 className="text-2xl font-medium mb-1">Medical Consent Notice</h1>
      <p className="text-sm text-gray-500 mb-6">
        This content appears in the "Read more" popup next to the medical-condition
        consent checkbox on the signup page.
      </p>

      <div className="mb-4">
        <label className="block pb-2 text-sm font-medium">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="w-full border border-gray-300 rounded-md p-2.5 text-sm"
        />
      </div>

      <div className="pb-2 text-sm font-medium">Content</div>
      {loaded && (
        <Editor
          apiKey="g64cjnqhg5z2xyt5dqqnvtvugubbr19suf3mljwa9h2ys1sr"
          onInit={(evt, editor) => (editorRef.current = editor)}
          initialValue={initialContent}
          init={{
            height: 350,
            menubar: true,
            plugins: [
              "advlist",
              "autolink",
              "lists",
              "link",
              "charmap",
              "preview",
              "anchor",
              "searchreplace",
              "visualblocks",
              "code",
              "fullscreen",
              "insertdatetime",
              "table",
              "help",
              "wordcount",
            ],
            toolbar:
              "undo redo | blocks | bold italic forecolor | " +
              "alignleft aligncenter alignright alignjustify | " +
              "bullist numlist outdent indent | removeformat | help",
            content_style:
              "body { font-size:14px; background-color: #fff; color: rgba(0, 0, 0, 0.7); }",
          }}
        />
      )}

      <div className="my-6 flex gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          type="button"
          className="font-medium rounded-lg text-sm px-10 py-2.5 text-center bg-[#186CED] text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default MedicalConsentManage;
